import type { IsoDate } from "../dates";
import type { AccountId, CategoryId, TransactionId } from "../ids";
import type { Account, Income, Transaction } from "../schemas";

/** Key of the built-in Uncategorised row. */
export const uncategorisedKey = "uncategorised";
export type BudgetRowKey = CategoryId | typeof uncategorisedKey;

/** One budget line (SPEC §5.1): a transaction or split line that affects the budget. */
export interface BudgetLine {
  txnId: TransactionId;
  /** Index into `splits`, or -1 for an unsplit transaction. */
  splitIndex: number;
  accountId: AccountId;
  date: IsoDate;
  amount: number;
  categoryId?: CategoryId;
  income?: Income;
}

/**
 * Extracts budget lines: transactions (or split lines) in on-budget accounts, excluding
 * transfers to other on-budget accounts.
 */
export function budgetLines(
  accounts: Iterable<Account>,
  transactions: Iterable<Transaction>,
): BudgetLine[] {
  const onBudget = new Map<string, boolean>();
  for (const a of accounts) {
    if (!a.deleted) {
      onBudget.set(a.id, a.onBudget);
    }
  }
  const out: BudgetLine[] = [];
  for (const t of transactions) {
    if (t.deleted || onBudget.get(t.accountId) !== true) {
      continue;
    }
    const { splits } = t.amountAndSplits;
    if (splits.length === 0) {
      if (t.transfer && onBudget.get(t.transfer.accountId) === true) {
        continue;
      }
      out.push(line(t, -1, t.amountAndSplits.amount, t.categoryId, t.income));
      continue;
    }
    splits.forEach((s, i) => {
      if (s.transferAccountId && onBudget.get(s.transferAccountId) === true) {
        return;
      }
      out.push(line(t, i, s.amount, s.categoryId, s.income));
    });
  }
  return out;
}

function line(
  t: Transaction,
  splitIndex: number,
  amount: number,
  categoryId: CategoryId | undefined,
  income: Income | undefined,
): BudgetLine {
  const l: BudgetLine = { txnId: t.id, splitIndex, accountId: t.accountId, date: t.date, amount };
  if (income) {
    l.income = income;
  } else if (categoryId) {
    l.categoryId = categoryId;
  }
  return l;
}
